import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { RootState } from "../reducers";
import Loading from "./Loading";
import {
  fetchTrackingSale,
  addSwap,
  swapProductsOrder,
  clearSwaps,
} from "../actions/orderActions";
import { useParams } from "react-router-dom";
import OrderProductsTable from "./OrderProductsTable";
import OrderInfoCard from "./OrderInfoCard";
import OrderClientCard from "./OrderClientCard";
import { fetchSimilarProducts } from "../actions/orderActions";
import ModalConfirmSwaps from "./ModalConfirmSwaps";
import useTenant from "../tenant/useTenant";
import { formatPrice } from "./utilities";

const TrackingSale = () => {
  const { saleId, clientId }: any = useParams();
  const tenant = useTenant();
  const dispatch: any = useDispatch();
  const isLoading = useSelector((state: RootState) => state.order.isLoading);
  const trackingOrder = useSelector(
    (state: RootState) => state.order.trackingOrder,
  );

  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const similarProducts = useSelector(
    (state: RootState) => state.order.similarProducts,
  );
  const swaps = useSelector((state: RootState) => state.order.swaps);
  const isSavingSwaps = useSelector(
    (state: RootState) => state.order.isSavingSwaps,
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  // Validar si los swaps están habilitados (menos de 10 días desde la compra)
  const swapsEnabled = useMemo(() => {
    //only for testing purposes Magali Client ID 2406
    // if (trackingOrder?.sale.client_id === 2406) {
    //   return true;
    // }
    if (!tenant.features.hasProductSwap) {
      return false;
    }
    const saleDate = trackingOrder?.sale.date;
    if (saleDate) {
      // Fecha límite: 10 días después de saleDate, hasta las 23:59:59
      const deadline = new Date(saleDate);
      deadline.setDate(deadline.getDate() + 10);
      deadline.setHours(23, 59, 59, 999);
      const now = new Date();
      return now <= deadline;
    }
    return false;
  }, [trackingOrder, tenant.features.hasProductSwap]);

  useEffect(() => {
    dispatch(fetchTrackingSale({ saleId, clientId }));
    return () => {
      dispatch(clearSwaps());
    };
  }, [dispatch, saleId, clientId]);

  const handleRowToggle = (index: number, productName: string) => {
    setExpandedRow(expandedRow === index ? null : index);
    dispatch(fetchSimilarProducts(productName));
  };

  const setSwapProduct = (args: {
    saleDetailId: string;
    productId: string;
    quantity: number;
    originalQuantity: number;
    newProductDescription: string;
    originalProductDescription: string;
  }) => {
    dispatch(
      addSwap({
        saleId,
        ...args,
      }),
    );
  };

  const handleSaveSwaps = () => {
    if (swaps && swaps.length > 0 && !isSavingSwaps) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmModal(false);
    dispatch(swapProductsOrder({ swaps })).then(() => {
      setShowSuccessAlert(true);
    });
  };

  return (
    <Container>
      {!isLoading ? (
        <>
          <Row className="mb-3">
            <Col md={6}>
              <OrderInfoCard trackingOrder={trackingOrder} />
            </Col>
            <Col md={6}>
              <OrderClientCard trackingOrder={trackingOrder} />
            </Col>
          </Row>
          <Row>
            <Col>
              <Card className="mb-3">
                <Card.Body>
                  <ModalConfirmSwaps
                    show={showConfirmModal}
                    swaps={swaps}
                    saleDetails={trackingOrder?.saleDetails}
                    isSaving={!!isSavingSwaps}
                    onCancel={() => setShowConfirmModal(false)}
                    onConfirm={handleConfirmSave}
                  />
                  {tenant.features.hasProductSwap &&
                    (!swapsEnabled ? (
                      <div className="alert alert-warning mt-3" role="alert">
                        <strong>Atención:</strong> El plazo para solicitar
                        cambios de productos es de 5 días desde la compra.
                      </div>
                    ) : showSuccessAlert ? (
                      <div className="alert alert-success" role="alert">
                        <strong>¡Solicitud enviada con éxito!</strong> Por
                        favor, revisa en el estado de tus pedidos y cuando este
                        listo para retirar acércate a nuestro local (España 1025
                        - Rosario) para finalizar y concretar el cambio de
                        productos con nuestro equipo. Recuerda que los cambios
                        no se realizan de manera automática ni a distancia.
                      </div>
                    ) : (
                      <div className="alert alert-info" role="alert">
                        <strong>Importante:</strong> Si deseas realizar un
                        cambio primero debes generar la solicitud aquí y luego
                        debes esperar a que te contactemos. No se realizan
                        cambios de manera automática ni a distancia.
                      </div>
                    ))}
                  <OrderProductsTable
                    trackingOrder={trackingOrder}
                    expandedRow={expandedRow}
                    similarProducts={similarProducts}
                    handleRowToggle={swapsEnabled ? handleRowToggle : () => {}}
                    setSwapProduct={setSwapProduct}
                    saveSwapsButton={
                      swapsEnabled && swaps && swaps.length > 0 ? (
                        <Button
                          variant="success"
                          className="my-2"
                          onClick={handleSaveSwaps}
                          disabled={isSavingSwaps}
                        >
                          {isSavingSwaps ? "Guardando..." : "Pedido de cambios"}
                        </Button>
                      ) : null
                    }
                  />

                  {trackingOrder?.sale?.subtotal !==
                    trackingOrder?.sale?.total && (
                    <div className="text-end mt-2">
                      <span className="fw-bold">Subtotal:</span> $
                      {formatPrice(trackingOrder?.sale?.subtotal)}
                      {typeof trackingOrder?.sale?.increase1 === "number" &&
                        trackingOrder.sale.increase1 > 0 &&
                        ` + ${trackingOrder.sale.increase1}%`}
                    </div>
                  )}
                  <div className="text-end mt-2">
                    <h5>Total: ${formatPrice(trackingOrder?.sale?.total)}</h5>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        <Loading />
      )}
    </Container>
  );
};
export default TrackingSale;
